import { Component, ElementRef, Inject, InjectionToken, Injector, OnDestroy, Optional, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, combineLatest, takeUntil } from 'rxjs';
import { OrganisationService } from '../../../../services/organisation.service';
import {
  Tutor,
  TutorService,
  TutorSettingItem,
} from '../../../../services/tutor.service';
import {
  CurriculumCoverage,
  CurriculumService,
} from '../../../../services/curriculum.service';
import {
  AI_SETTINGS_SECTIONS,
  HIDDEN_VARIABLES,
  SECTIONS_ELSEWHERE,
  SettingsSection,
  SettingsSectionTab,
  SettingsTile,
  VIRTUAL_BOT_SETTINGS_BY_VAR,
  filterSectionsByAvailableSettings,
  ANCHORED_SETTINGS,
  withAnchoredSettings,
} from './ai-settings.sections';

/** Per-learner TOKEN limits (day / month). Superseded by the credit limits
 *  `max_credits_per_user_*`; shown only while switched on (Lasse 2026-10-05).
 *  Their number fields follow these toggles through `visibility_rule`. */
const LEGACY_TOKEN_LIMIT_TOGGLES = new Set([
  'max_tokens_per_user_day_enabled',
  'max_tokens_per_user_month_enabled',
]);

/** Settings whose multi-select is rendered inline by the boolean toggle row */
const LINKED_SETTINGS: Record<string, string> = {
  'Enable Tabs': 'Enabled Tabs',
};

const TAB_SETTING_VARIABLES = new Set([
  'tabs_enabled',
  'enabled_tabs',
  'tab_exercises_enabled',
  'tab_messages_enabled',
  'tab_notifications_enabled',
  'tab_materials_enabled',
  // Study Plan + Learner Profile tabs (migration 124) are twin-inheritable too
  // (session.TWIN_TAB_KEYS), so recognise them as tab settings. studyplan is
  // hidden/auto-derived; learnerprofile is a real per-twin toggle.
  'tab_studyplan_enabled',
  'tab_learnerprofile_enabled',
]);

// Materials (Lernmaterialien) configuration settings. They live on the
// linked alpha_dozent twin like the tab flags, but are NOT tab toggles —
// kept separate so the widget-tabs tile layout doesn't apply to them.
const MATERIALS_SETTING_VARIABLES = new Set([
  'materials_allowed_types',
  'materials_clarify_enabled',
  'materials_suggestions_enabled',
  'materials_download_enabled',
  'materials_max_saved_total_enabled',
  'materials_max_saved_total',
  'materials_max_generations_per_month_enabled',
  'materials_max_generations_per_month',
]);

/** Additive granular model picker (provider → concrete models + credit
 *  hints, `app-model-selector`) shown in place of the plain `ai_provider`
 *  dropdown. Flip to `false` to restore the original dropdown everywhere —
 *  no other change needed (the selector writes the same `ai_provider`
 *  setting, so routing is identical either way). */
const USE_GRANULAR_MODEL_SELECTOR = true;

interface RenderedTile {
  titleKey?: string;
  hintKey?: string;
  settings: TutorSettingItem[];
  targetTutor?: Tutor;
  linkedSettings?: Record<string, TutorSettingItem>;
  disabled?: boolean;
  disabledHintKey?: string;
  /** Per-setting disable: variable_name → hint i18n key. Wins over the
   *  tile-wide flag for individual rows (e.g. the messages tab on
   *  tutor/flex, which has no recipient without a linked Twin). */
  disabledSettings?: Record<string, string>;
  isWidgetTabs?: boolean;
  /** Mirrors `SettingsTile.kind` so the template can render the
   *  matching custom component (e.g. the FAQ list editor). */
  kind?: 'faq';
  /** Mirrors `SettingsTile.tab`. */
  tab?: string;
}

interface RenderedTab extends SettingsSectionTab {
  tiles: RenderedTile[];
}

interface RenderedSection extends SettingsSection {
  renderedTiles: RenderedTile[];
  /** Section tabs that have at least one tile; empty = no tab bar. */
  renderedTabs: RenderedTab[];
}

/** Ein Abschnitt der KI-Konfiguration als Reiter einer anderen Seite
 *  (Agent › Wissen › „Quellen & Suche" = Abschnitt `knowledge`). Der Gastgeber
 *  stellt Menü, Karte und Reiter und liefert den Abschnitt über diesen Token —
 *  als Token statt Input, weil die Komponente den Abschnitt schon im
 *  Konstruktor aus der Route liest (dort wären Inputs noch leer, und die
 *  Rückfall-Navigation würde auf einen anderen Abschnitt springen). */
export const AI_SETTINGS_EMBEDDED_SECTION = new InjectionToken<string>(
  'AI_SETTINGS_EMBEDDED_SECTION'
);

@Component({
  selector: 'app-ai-settings',
  templateUrl: './ai-settings.component.html',
  styleUrl: './ai-settings.component.scss',
})
export class AiSettingsComponent implements OnDestroy {
  tutor: Tutor | undefined;

  /** Templates that may attach a dozent. Mirrors the backend whitelist
   *  in `crud.dozent_link_ops.TUTOR_TEMPLATE_NAMES`. */
  private static readonly LINKABLE_TEMPLATES = new Set<string>([
    'alpha_tutor',
    'alpha_flexible',
  ]);

  /** True when the active tutor uses a template that can link to a
   *  dozent — drives the visibility of the dozent-link picker tile. */
  get isLinkableTemplate(): boolean {
    if (!this.tutor?.template_id) return false;
    const template = this.tutorService.tutorTemplates$.value.find(
      (t) => t.id === this.tutor!.template_id
    );
    return !!template && AiSettingsComponent.LINKABLE_TEMPLATES.has(template.name);
  }

  /** True when the active bot is an Alpha Twin (alpha_dozent template).
   *  Drives Twin-specific copy variants on shared section descriptions
   *  (e.g. "Basis & Persona" → "Gib diesem Twin eine Persönlichkeit…"). */
  get isTwin(): boolean {
    if (!this.tutor?.template_id) return false;
    const template = this.tutorService.tutorTemplates$.value.find(
      (t) => t.id === this.tutor!.template_id
    );
    return !!template && template.name === 'alpha_dozent';
  }

  /** Whether this client has the `DOZENT` feature unlocked.
   *  Drives the Coming-Soon visualisation of the Dozent section on
   *  alpha_tutor / alpha_flexible — section stays visible (so admins
   *  see what the upgrade includes) but every input is disabled. */
  get hasDozentFeature(): boolean {
    return this.organisationService.hasFeature('DOZENT');
  }

  /** Feature flag: render the granular `app-model-selector` instead of the
   *  plain `ai_provider` dropdown. Single revert point. */
  get useGranularModelSelector(): boolean {
    return USE_GRANULAR_MODEL_SELECTOR;
  }

  /** The `ai_provider` setting inside a tile, but only when the granular
   *  selector is active and a tutor is loaded — else null so the template
   *  keeps the original dropdown. Drives the tile-level swap. */
  providerSettingFor(tile: RenderedTile): TutorSettingItem | null {
    if (!this.useGranularModelSelector || !this.tutor) return null;
    return (
      tile.settings.find((s) => s.variable_name === 'ai_provider') || null
    );
  }

  /** True when this tile carries the Twin's Moodle lecturer switch, so the
   *  template can append the person picker under the generic rows. The list
   *  lives in `bot_moodle_dozent_access`, not in `template_settings`. */
  isMoodleDozentTile(tile: RenderedTile): boolean {
    return tile.settings.some(
      (s) => s.variable_name === 'moodle_dozent_tools_enabled'
    );
  }

  /** Whether that switch is currently on — the picker is pointless while the
   *  Twin offers no Moodle functions at all. Falls back to the template
   *  default when the bot has no stored value yet. */
  moodleDozentEnabled(tile: RenderedTile): boolean {
    const setting = tile.settings.find(
      (s) => s.variable_name === 'moodle_dozent_tools_enabled'
    );
    if (!setting) return false;
    const raw = setting.value !== undefined && setting.value !== null
      ? setting.value
      : setting.default_value;
    return raw === true || raw === 'true';
  }

  /** The saved audience, which decides whether the picker edits or only
   *  previews. Unsaved dropdown changes are not reflected on purpose: the
   *  list is written through its own endpoint, and acting on a value the
   *  backend has not stored yet would be a lie. */
  moodleDozentAudience(tile: RenderedTile): string {
    const setting = tile.settings.find(
      (s) => s.variable_name === 'moodle_dozent_audience'
    );
    const raw = setting?.value ?? setting?.default_value ?? 'all_teachers';
    return typeof raw === 'string' ? raw : 'all_teachers';
  }

  /** True when a setting row is the `ai_provider` dropdown that the granular
   *  selector replaces — used to suppress the generic renderer for it. */
  isSuppressedByModelSelector(setting: TutorSettingItem): boolean {
    return (
      this.useGranularModelSelector &&
      !!this.tutor &&
      setting.variable_name === 'ai_provider'
    );
  }

  /** Sections with actual content for this tutor, driven by SECTIONS layout. */
  availableSections: SettingsSection[] = [];

  activeSection: RenderedSection | undefined;
  activeSectionKey: string | null = null;

  /** Open tab of a section with `tabs` (e.g. Modell & Limits). */
  activeTabKey: string | null = null;

  /** Leftovers that didn't map to any known tile — shown at the bottom. */
  otherTile: RenderedTile | null = null;

  /** toggle name → linked multi-select setting */
  linkedSettings: Record<string, TutorSettingItem> = {};

  /** Settings that belong to the currently attached alpha_dozent bot.
   *  On alpha_tutor / alpha_flexible the visible "Funktionen" section
   *  edits these values because session-init reads them from the linked
   *  dozent, not from the tutor bot itself. */
  linkedDozentSettings: TutorSettingItem[] = [];
  linkedDozentTutor: Tutor | undefined;
  attachedDozentId: string | null = null;

  private destroy$ = new Subject<void>();
  settingsCache: TutorSettingItem[] = [];
  private loadingDozentSettingsFor: string | null = null;
  private readonly noTwinTabsHintKey =
    'enums.aiConfigurationTiles.chat_tabs_requires_twin';

  /** What the curriculum switch on this agent actually reaches. Shown next to
   *  the switch, because "switched on and nothing happens" is nearly always a
   *  missing release on the curriculum side and the operator has no way to
   *  see that from here otherwise. */
  curriculumCoverage: CurriculumCoverage | null = null;

  constructor(
    protected tutorService: TutorService,
    protected activeRoute: ActivatedRoute,
    private router: Router,
    private organisationService: OrganisationService,
    private curriculumService: CurriculumService,
    private injector: Injector,
    @Optional() @Inject(AI_SETTINGS_EMBEDDED_SECTION)
    readonly embeddedSection: string | null,
  ) {
    // Own route first (old links), else the agent shell (`tutors/:tutorId`).
    const tutorId =
      this.activeRoute.snapshot.paramMap.get('tutorId') ??
      this.activeRoute.parent?.snapshot.paramMap.get('tutorId');

    if (!this.tutorService.tutorTemplates$.value.length) {
      this.tutorService.listTutorTemplates().subscribe();
    }

    combineLatest([
      this.tutorService.tutors$,
      this.tutorService.tutorTemplates$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.tutor = this.tutorService.findTutor(tutorId!);
        this.loadCurriculumCoverage();
        this.refreshLinkedDozentSettings();
        // Re-render in case the active section already has a virtual
        // bot-backed setting whose value depends on `this.tutor`.
        this.rebuildActiveSection();
      });

    // Recompute whenever settings OR the `section` route param changes.
    // Section is part of the URL path (`/ai-settings/<section>`) so the
    // tutor-menu's nb-menu can highlight it via Nebular's path matching.
    combineLatest([
      this.tutorService.tutorSettings$,
      this.activeRoute.paramMap,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([settings, params]) => {
        this.settingsCache = settings;
        this.availableSections = filterSectionsByAvailableSettings(settings, {
          isLinkableTutor: this.isLinkableTemplate,
        });

        // Eingebettet steht der Abschnitt fest; sonst kommt er aus der URL.
        const requested = this.embeddedSection ?? params.get('section');

        // Empty settings = fetch still in flight (tutor-menu resets the
        // subject before fetching, see 67bbcfc5). The section list then
        // contains only virtual sections ('dozent'/'functions'/'guardrails'),
        // so a real section like 'model_limits' would look invalid and the
        // fallback below would bounce the user to 'dozent'. Keep whatever
        // the URL says and wait for the real emission before validating.
        if (!settings.length) {
          this.activeSectionKey = requested;
          this.rebuildActiveSection();
          return;
        }

        const isValid =
          requested && this.availableSections.some((s) => s.key === requested);
        // Eingebettet nie auf einen anderen Abschnitt ausweichen: gibt es
        // den festen hier nicht, bleibt der Reiter leer ("keine Einstellungen").
        // Rückfall nie auf einen Abschnitt, der jetzt Reiter einer anderen
        // Seite ist (SECTIONS_ELSEWHERE): /ai-settings/<der> leitet dorthin
        // um — KI-Konfiguration spränge sonst auf „Wissen" oder „Lernprozess".
        this.activeSectionKey = isValid
          ? requested
          : this.embeddedSection
            ? null
            : this.availableSections.find((s) => !(s.key in SECTIONS_ELSEWHERE))?.key ?? null;

        // No (valid) section in the URL — redirect to the default so the
        // sub-menu reflects current state from first render.
        if (!isValid && this.activeSectionKey && tutorId && !this.embeddedSection) {
          this.router.navigate(
            ['/tutors', tutorId, 'ai-settings', this.activeSectionKey],
            { replaceUrl: true }
          );
          return;
        }

        this.rebuildActiveSection();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Stable identity for *ngFor of tiles so toggling a single setting
   *  doesn't tear down and recreate every sibling tile (which would
   *  destroy the in-flight FormGroups inside `<app-tutor-settings>`,
   *  drop the user's cursor, and "blink" toggles to their server value).
   */
  trackTile = (i: number, t: RenderedTile) => t.titleKey ?? `__notitle_${i}`;

  /** Same idea per setting — `setting_id` is stable across re-renders,
   *  the component instance gets reused, the form keeps its state.
   */
  trackSetting = (_: number, s: TutorSettingItem) => s.setting_id;

  trackTab = (_: number, t: RenderedTab) => t.key;

  onSectionTabChange(tab: { tabId?: string }): void {
    if (tab?.tabId) this.activeTabKey = tab.tabId;
  }

  private rebuildActiveSection(): void {
    if (!this.activeSectionKey) {
      this.activeSection = undefined;
      this.otherTile = null;
      return;
    }

    const byVar = new Map<string, TutorSettingItem>();
    const byName = new Map<string, TutorSettingItem>();
    for (const s of this.settingsCache) {
      if (s.variable_name) byVar.set(s.variable_name, s);
      byName.set(s.name, s);
    }

    // Only hide a linked multi-select when its paired toggle is
    // actually present for this template — otherwise templates that
    // ship only the multi-select (e.g. alpha_flexible's `enabled_tabs`
    // without a separate `tabs_enabled`) lose the field entirely.
    const hiddenNames = new Set<string>();
    this.linkedSettings = {};
    for (const [toggle, multi] of Object.entries(LINKED_SETTINGS)) {
      const toggleSetting = byName.get(toggle);
      const ms = byName.get(multi);
      if (toggleSetting && ms) {
        hiddenNames.add(multi);
        this.linkedSettings[toggle] = ms;
      }
    }

    // Build `other` tile across ALL sections so it reflects things the UI
    // doesn't know about yet, regardless of which section is active.
    const claimedAcrossApp = new Set<string>();
    for (const sec of AI_SETTINGS_SECTIONS) {
      for (const t of sec.tiles) {
        for (const v of t.variables) claimedAcrossApp.add(v);
      }
    }
    // Placed after their anchor, never in a tile list of their own.
    for (const v of Object.keys(ANCHORED_SETTINGS)) claimedAcrossApp.add(v);
    const leftovers = this.settingsCache.filter(
      (s) =>
        s.variable_name &&
        !claimedAcrossApp.has(s.variable_name) &&
        !hiddenNames.has(s.name) &&
        !HIDDEN_VARIABLES.has(s.variable_name)
    );
    this.otherTile =
      leftovers.length && this.activeSectionKey === this.availableSections[this.availableSections.length - 1].key
        ? { titleKey: 'enums.aiConfigurationTiles.other', settings: leftovers }
        : null;

    const section = AI_SETTINGS_SECTIONS.find(
      (s) => s.key === this.activeSectionKey
    );
    if (!section) {
      this.activeSection = undefined;
      return;
    }

    let renderedTiles: RenderedTile[] = section.tiles
      .map((tile) => this.renderTile(tile, byVar, hiddenNames))
      .filter(
        (t): t is RenderedTile =>
          !!t && (t.settings.length > 0 || t.kind === 'faq')
      );

    if (section.key === 'curriculum') {
      renderedTiles = this.withCurriculumGating(renderedTiles, byVar);
    }

    if (section.key === 'functions') {
      if (this.isLinkableTemplate) {
        // Tutor/flex now carry their own tab settings (migration 095). When
        // a Twin is linked, the owner flag `use_twin_widget_tabs` decides
        // which source drives the widget tabs.
        const useTwinTabs = this.resolveUseTwinTabs(byVar);
        if (this.attachedDozentId && useTwinTabs) {
          // Tabs come from the linked Twin (default, backwards-compatible).
          // Its tile list covers tabs and materials. The Moodle actions,
          // which stay this agent's own, used to be appended here; they are
          // their own section now (`moodle_actions`, a tab of Verhalten &
          // Fähigkeiten) and no longer part of this branch.
          renderedTiles = this.renderLinkedDozentFunctionTiles();
        } else {
          // The tutor/flex bot manages its own tabs.
          renderedTiles = this.withMaterialsTileGating(renderedTiles, byVar);
          // Direct messages have no recipient on tutor/flex own-tab bots —
          // grey out the messages tab toggle (Twin-driven tabs above keep
          // it active, messages route to the Twin there).
          renderedTiles = this.withMessagesTabGating(renderedTiles);
        }
        // With a Twin attached, surface the source toggle above the tiles.
        if (this.attachedDozentId) {
          const toggleTile = this.buildTwinTabsToggleTile(byVar);
          if (toggleTile) renderedTiles = [toggleTile, ...renderedTiles];
        }
      } else {
        // alpha_dozent (the Twin) manages its own tabs directly.
        renderedTiles = this.withMaterialsTileGating(renderedTiles, byVar);
      }
    }

    // Twin (alpha_dozent) gets a few section-specific copy overrides:
    //   - basis_persona: replace generic "Wer ist der Tutor…" with a
    //     Twin-flavored variant
    //   - guardrails ("Sicherheit"): hide the description entirely
    //     (template's *ngIf="section.descKey" suppresses the <p>)
    // Other sections fall through to the shared copy.
    let descKey: string | undefined = section.descKey;
    if (this.isTwin) {
      if (section.key === 'basis_persona') {
        descKey = 'enums.aiConfigurationSections.basis_persona.descTwin';
      } else if (section.key === 'guardrails') {
        descKey = undefined;
      }
    } else if (this.isLinkableTemplate && section.key === 'functions') {
      descKey = 'enums.aiConfigurationSections.functions.descTutor';
    }

    // Embedded sections are already a tab of their host page — no second bar.
    const firstTab = section.tabs?.[0]?.key;
    const renderedTabs: RenderedTab[] = this.embeddedSection
      ? []
      : (section.tabs ?? [])
          .map((tab) => ({
            ...tab,
            tiles: renderedTiles.filter((t) => (t.tab ?? firstTab) === tab.key),
          }))
          .filter((tab) => tab.tiles.length > 0);
    if (!renderedTabs.some((t) => t.key === this.activeTabKey)) {
      this.activeTabKey = renderedTabs[0]?.key ?? null;
    }

    this.activeSection = {
      ...section,
      descKey: descKey ?? '',
      renderedTiles,
      // A single surviving tab is no choice — render the tiles plainly.
      renderedTabs: renderedTabs.length > 1 ? renderedTabs : [],
    };
  }

  private renderTile(
    tile: SettingsTile,
    byVar: Map<string, TutorSettingItem>,
    hiddenNames: Set<string>,
    targetTutor?: Tutor,
    tileLinkedSettings?: Record<string, TutorSettingItem>,
  ): RenderedTile | null {
    const settings: TutorSettingItem[] = [];
    // Variables already rendered in THIS tile — a setting is only shown as an
    // indented sub-option when its master toggle is one of them (see
    // `markSubOption`).
    const renderedInTile = new Set<string>();
    for (const v of withAnchoredSettings(tile.variables, (x) => byVar.has(x))) {
      if (HIDDEN_VARIABLES.has(v)) continue;

      // Virtual bot-backed setting: synthesize a TutorSettingItem on the
      // fly so the standard field renderer can show it.
      const virtual = VIRTUAL_BOT_SETTINGS_BY_VAR[v];
      if (virtual && this.tutor) {
        settings.push({
          setting_id: virtual.variable_name,
          name: virtual.name,
          variable_name: virtual.variable_name,
          field_type: virtual.field_type,
          is_required: false,
          default_value: virtual.default_value,
          value:
            (this.tutor as any)[virtual.bot_field] ?? virtual.default_value,
          options: [],
          template_id: '',
          _isSubOption: false,
        } as TutorSettingItem);
        renderedInTile.add(v);
        continue;
      }

      const s = byVar.get(v);
      if (!s || hiddenNames.has(s.name)) continue;
      // Per-learner limits are set in credits. The older token limits stay
      // visible only on agents that have one switched on, so an existing
      // limit can still be seen and turned off — nobody sets a new one.
      if (LEGACY_TOKEN_LIMIT_TOGGLES.has(v) && !(s.value === true || s.value === 'true')) continue;
      // Respect visibility_rule on conditional settings (e.g. the
      // number inputs paired with their *_enabled toggles — see
      // migration 019). Without this, the number field would render
      // even when its toggle is off.
      if (!this.isSettingVisible(s, byVar)) continue;
      // Twin (alpha_dozent) gets a renamed label/hint for the
      // `teacher_introduction` field — it's no longer the learner-
      // facing greeting, it's the Twin's own self-description in
      // the system prompt. Re-point the i18n lookup via a cloned
      // setting with a Twin-suffixed `name`. New keys live alongside
      // the original under `enums.aiConfiguration` / Hints.
      this.markSubOption(s, renderedInTile);
      if (this.isTwin && s.variable_name === 'teacher_introduction') {
        settings.push({ ...s, name: 'Teacher Introduction Twin' });
        renderedInTile.add(v);
        continue;
      }
      settings.push(s);
      renderedInTile.add(v);
    }
    return {
      titleKey: tile.titleKey,
      hintKey: tile.hintKey,
      kind: tile.kind,
      tab: tile.tab,
      settings,
      targetTutor,
      linkedSettings: tileLinkedSettings,
      isWidgetTabs: tile.variables.some((v) => TAB_SETTING_VARIABLES.has(v)),
    };
  }

  private renderLinkedDozentFunctionTiles(): RenderedTile[] {
    if (!this.linkedDozentTutor) return [];
    const targetTutor = this.linkedDozentTutor;

    const byVar = new Map<string, TutorSettingItem>();
    const byName = new Map<string, TutorSettingItem>();
    for (const s of this.linkedDozentSettings) {
      if (s.variable_name) byVar.set(s.variable_name, s);
      byName.set(s.name, s);
    }

    const hiddenNames = new Set<string>();
    const linkedSettings: Record<string, TutorSettingItem> = {};
    for (const [toggle, multi] of Object.entries(LINKED_SETTINGS)) {
      const toggleSetting = byName.get(toggle);
      const ms = byName.get(multi);
      if (toggleSetting && ms) {
        hiddenNames.add(multi);
        linkedSettings[toggle] = ms;
      }
    }

    const section = AI_SETTINGS_SECTIONS.find((s) => s.key === 'functions');
    if (!section) return [];

    const tiles = section.tiles
      .filter((tile) =>
        tile.variables.some(
          (v) =>
            TAB_SETTING_VARIABLES.has(v) || MATERIALS_SETTING_VARIABLES.has(v)
        )
      )
      .map((tile) =>
        this.renderTile(
          tile,
          byVar,
          hiddenNames,
          targetTutor,
          linkedSettings,
        )
      )
      .filter(
        (t): t is RenderedTile =>
          !!t && (t.settings.length > 0 || t.kind === 'faq')
      );
    return this.withMaterialsTileGating(tiles, byVar);
  }

  /** Owner flag: does a linked Twin drive the widget tabs? Reads the
   *  tutor/flex bot's own `use_twin_widget_tabs` (default true). */
  private resolveUseTwinTabs(
    byVar: Map<string, TutorSettingItem>
  ): boolean {
    const s = byVar.get('use_twin_widget_tabs');
    const raw = s?.value ?? s?.default_value ?? true;
    return raw === true || raw === 'true' || raw === 1 || raw === '1';
  }

  /** Source-toggle tile shown only when a Twin is attached — lets the
   *  admin choose between the linked Twin's tabs and this bot's own.
   *  Saved to the tutor/flex bot (`this.tutor`). */
  private buildTwinTabsToggleTile(
    byVar: Map<string, TutorSettingItem>
  ): RenderedTile | null {
    const setting = byVar.get('use_twin_widget_tabs');
    if (!setting || !this.tutor) return null;
    return {
      titleKey: 'enums.aiConfigurationTiles.tabs_source',
      hintKey: 'enums.aiConfigurationTiles.tabs_source_hint',
      settings: [setting],
      targetTutor: this.tutor,
      isWidgetTabs: false,
    };
  }

  /** Grey out the materials-configuration tile while the Lernmaterialien
   *  tab toggle is off — the settings only take effect with the tab, so
   *  they render disabled instead of hidden (admin still sees the shape). */
  private withMaterialsTileGating(
    tiles: RenderedTile[],
    byVar: Map<string, TutorSettingItem>
  ): RenderedTile[] {
    const flag = byVar.get('tab_materials_enabled');
    const raw = flag?.value ?? flag?.default_value;
    const tabOn = raw === true || raw === 'true' || raw === 1 || raw === '1';
    if (tabOn) return tiles;
    return tiles.map((tile) =>
      tile.settings.some((s) =>
        MATERIALS_SETTING_VARIABLES.has(s.variable_name || '')
      )
        ? {
            ...tile,
            disabled: true,
            disabledHintKey:
              'enums.aiConfigurationTiles.materials_requires_tab',
          }
        : tile
    );
  }

  /** The learner-side curriculum setting needs the learner profile: without
   *  it there is no enrolment and no goal progress to report. The dependency
   *  used to hide the setting; in a section of its own a half-empty page
   *  reads as a bug, so it renders disabled with the reason instead. */
  private withCurriculumGating(
    tiles: RenderedTile[],
    byVar: Map<string, TutorSettingItem>
  ): RenderedTile[] {
    const profile = byVar.get('learner_profile_enabled');
    const raw = profile?.value ?? profile?.default_value ?? true;
    const profileOn = raw === true || raw === 'true' || raw === 1 || raw === '1';
    if (profileOn) return tiles;
    return tiles.map((tile) =>
      tile.settings.some((s) => s.variable_name === 'curriculum_awareness')
        ? {
            ...tile,
            disabledSettings: {
              ...tile.disabledSettings,
              curriculum_awareness:
                'enums.aiConfigurationTiles.curriculum_requires_profile',
            },
          }
        : tile
    );
  }

  /** Grey out the messages-tab toggle on tutor/flex own-tab tiles: direct
   *  messages need a linked Alpha Twin as recipient, which these bots
   *  don't provide. Value is left untouched — only the control locks. */
  private withMessagesTabGating(tiles: RenderedTile[]): RenderedTile[] {
    return tiles.map((tile) =>
      tile.settings.some((s) => s.variable_name === 'tab_messages_enabled')
        ? {
            ...tile,
            disabledSettings: {
              ...tile.disabledSettings,
              tab_messages_enabled:
                'enums.aiConfigurationTiles.messages_requires_twin',
            },
          }
        : tile
    );
  }

  private withDisabledNoTwinTabTile(tiles: RenderedTile[]): RenderedTile[] {
    let foundTabTile = false;
    const markedTiles = tiles.map((tile) => {
      if (!this.tileContainsTabSettings(tile)) return tile;
      foundTabTile = true;
      return {
        ...tile,
        disabled: true,
        disabledHintKey: this.noTwinTabsHintKey,
      };
    });

    if (foundTabTile) return markedTiles;

    return [
      ...markedTiles,
      {
        titleKey: 'enums.aiConfigurationTiles.chat_tabs',
        hintKey: 'enums.aiConfigurationTiles.chat_tabs_hint',
        settings: this.createDisabledTabSettings(),
        disabled: true,
        disabledHintKey: this.noTwinTabsHintKey,
      },
    ];
  }

  private tileContainsTabSettings(tile: RenderedTile): boolean {
    const direct = tile.settings.some((setting) =>
      TAB_SETTING_VARIABLES.has(setting.variable_name || '')
    );
    const linked = Object.values(tile.linkedSettings || {}).some((setting) =>
      TAB_SETTING_VARIABLES.has(setting.variable_name || '')
    );
    return direct || linked;
  }

  private createDisabledTabSettings(): TutorSettingItem[] {
    return [
      ['Exercises Tab', 'tab_exercises_enabled'],
      ['Messages Tab', 'tab_messages_enabled'],
      ['Notifications Tab', 'tab_notifications_enabled'],
    ].map(([name, variableName]) => ({
      setting_id: `__disabled_${variableName}`,
      name,
      variable_name: variableName,
      field_type: 'boolean',
      is_required: false,
      default_value: false,
      value: false,
      options: [],
      template_id: '',
    }));
  }

  onDozentLinkChanged(dozentId: string | null): void {
    this.attachedDozentId = dozentId;
    this.linkedDozentSettings = [];
    this.linkedDozentTutor = undefined;
    if (dozentId) {
      this.loadLinkedDozentSettings(dozentId);
    } else {
      this.rebuildActiveSection();
    }
  }

  /** Live state of the `bot_faq_enabled` toggle — drives the greyed-out
   *  state of the FAQ editor below it. Reads from the cached settings so
   *  it reflects the current (possibly just-toggled) value. */
  get faqEnabled(): boolean {
    const setting = this.settingsCache.find(
      (s) => s.variable_name === 'bot_faq_enabled',
    );
    if (!setting) return true;
    const v = setting.value ?? setting.default_value;
    return v === true || v === 'true' || v === 1 || v === '1';
  }

  onSettingValueSaved(event: {
    tutorId: string;
    settingId: string;
    value: any;
  }): void {
    // Keep the local cache for the main tutor in sync so getters like
    // `faqEnabled` react immediately to a toggle without a full reload.
    if (this.tutor && event.tutorId === this.tutor.id) {
      this.settingsCache = this.settingsCache.map((s) =>
        String(s.setting_id) === event.settingId
          ? { ...s, value: event.value }
          : s,
      );
      const saved = this.settingsCache.find((s) => String(s.setting_id) === event.settingId);
      if (saved?.variable_name === 'knowledge_mode') this.focusTopicFieldIfEmpty();
      // A change to the bot's own settings (notably the
      // `use_twin_widget_tabs` source toggle) can switch which tab source
      // the functions section renders — rebuild so it reflects instantly.
      this.rebuildActiveSection();
    }
    if (!this.attachedDozentId || event.tutorId !== this.attachedDozentId) {
      return;
    }
    this.linkedDozentSettings = this.linkedDozentSettings.map((setting) =>
      String(setting.setting_id) === event.settingId
        ? { ...setting, value: event.value }
        : setting
    );
    this.rebuildActiveSection();
  }

  private refreshLinkedDozentSettings(): void {
    if (!this.tutor || !this.isLinkableTemplate) {
      this.attachedDozentId = null;
      this.linkedDozentSettings = [];
      this.linkedDozentTutor = undefined;
      this.loadingDozentSettingsFor = null;
      return;
    }

    const tutorId = this.tutor.id;
    if (this.loadingDozentSettingsFor === tutorId) return;
    this.loadingDozentSettingsFor = tutorId;

    this.tutorService.getAttachedDozent(tutorId).subscribe({
      next: (attached) => {
        this.loadingDozentSettingsFor = null;
        const dozentId = attached?.dozent_bot_id ?? null;
        if (dozentId === this.attachedDozentId && this.linkedDozentTutor) {
          return;
        }
        this.onDozentLinkChanged(dozentId);
      },
      error: () => {
        this.loadingDozentSettingsFor = null;
        this.onDozentLinkChanged(null);
      },
    });
  }

  private loadLinkedDozentSettings(dozentId: string): void {
    this.tutorService.fetchTutorSettings(dozentId).subscribe({
      next: (settings) => {
        this.linkedDozentSettings = settings;
        this.linkedDozentTutor = this.tutor
          ? { ...this.tutor, id: dozentId }
          : undefined;
        this.rebuildActiveSection();
      },
      error: () => {
        this.linkedDozentSettings = [];
        this.linkedDozentTutor = undefined;
        this.rebuildActiveSection();
      },
    });
  }

  /** True when the setting is rendered as an indented sub-option of the toggle
   *  above it. Set in `renderTile` (`markSubOption`) — NOT derived from
   *  `visibility_rule` alone: a setting may be gated by a master that lives in
   *  a different section entirely (e.g. the Lernplan tab depends on the
   *  Didaktik master `study_path_recommendation_enabled`), and indenting it
   *  under whatever toggle happens to precede it in the tile claims a
   *  parent-child relationship that does not exist. */
  isDependentSetting(setting: TutorSettingItem): boolean {
    return !!(setting as any)._isSubOption;
  }

  /** Stamp `_isSubOption`: only settings whose visibility master was already
   *  rendered IN THE SAME TILE are indented + railed under it. Always writes
   *  the flag (both branches) so a re-render can't leave a stale one behind. */
  private markSubOption(s: TutorSettingItem, renderedInTile: Set<string>): void {
    const when = (s as any).visibility_rule?.when;
    (s as any)._isSubOption = !!when && renderedInTile.has(when);
  }

  /** Evaluate a setting's visibility_rule against the *current* values in
   *  the byVar map. Mirrors the wizard's `isVisible` logic so admins see
   *  identical conditional behavior on both surfaces.
   *  Rule shape: { when: "<variable_name>", equals: <value> } |
   *               { when: "<variable_name>", not_equals: <value> } */
  private isSettingVisible(
    setting: TutorSettingItem,
    byVar: Map<string, TutorSettingItem>
  ): boolean {
    const rule = (setting as any).visibility_rule as
      | { when: string; equals?: any; not_equals?: any }
      | null
      | undefined;
    if (!rule || !rule.when) return true;
    const dep = byVar.get(rule.when);
    // If the dependency isn't loaded yet, fall back to the default value
    // declared on the rule's referenced setting (so initial render isn't
    // empty).
    const actual =
      dep?.value !== undefined && dep?.value !== null
        ? dep.value
        : dep?.default_value;
    if ('equals' in rule) return actual === rule.equals;
    if ('not_equals' in rule) return actual !== rule.not_equals;
    return true;
  }
  private loadCurriculumCoverage(): void {
    const tutorId = this.tutor?.id;
    if (!tutorId) {
      this.curriculumCoverage = null;
      return;
    }
    this.curriculumService.botCurriculumCoverage(tutorId).subscribe({
      next: (coverage) => (this.curriculumCoverage = coverage),
      // A missing status line must not break the settings page.
      error: () => (this.curriculumCoverage = null),
    });
  }

  /** True for the tile that carries the curriculum switch, so the status line
   *  appears next to it and nowhere else. */
  isCurriculumTile(tile: { settings?: { variable_name?: string }[] }): boolean {
    return !!tile.settings?.some(
      (s) => s.variable_name === 'curriculum_context_enabled'
    );
  }

  get curriculumUncovered(): number {
    if (!this.curriculumCoverage) return 0;
    return Math.max(
      0,
      this.curriculumCoverage.courses_total - this.curriculumCoverage.courses_covered
    );
  }

  /** True for the tile that carries „Einschränkungen der Wissensbasis". */
  isKnowledgeModeTile(tile: { settings?: { variable_name?: string }[] }): boolean {
    return !!tile.settings?.some((s) => s.variable_name === 'knowledge_mode');
  }

  private settingValue(variable: string): unknown {
    const s = this.settingsCache.find((x) => x.variable_name === variable);
    return s ? (s.value ?? s.default_value) : undefined;
  }

  /** „Themenspezifisch" only works with a „Fachgebiet": without one the
   *  backend runs the agent as „Nur Wissensbasis" (al-tutor
   *  `session.normalize_knowledge_mode_settings`, al-tutor-lgs
   *  `shared/grounding.knowledge_mode`). While that mode is chosen the field
   *  sits right below the setting (same `expert_topic` row as on Basis &
   *  Persona) and a warning says which mode really applies. `topicSetting`
   *  is missing for templates without the field (alpha_assist, alpha_support)
   *  — there the mode always falls back. */
  get topicModeNote(): { topic: string; topicSetting: TutorSettingItem | null } | null {
    if (this.settingValue('knowledge_mode') !== 'strict_topic') return null;
    const raw = this.settingValue('expert_topic');
    return {
      topic: typeof raw === 'string' ? raw.trim() : '',
      topicSetting: this.settingsCache.find((s) => s.variable_name === 'expert_topic') ?? null,
    };
  }

  private readonly host = inject(ElementRef<HTMLElement>);

  /** Right after „Themenspezifisch" was picked without a Fachgebiet: put the
   *  cursor into the field below, so the next keystroke fills it. */
  private focusTopicFieldIfEmpty(): void {
    const note = this.topicModeNote;
    if (!note || note.topic || !note.topicSetting) return;
    setTimeout(() => {
      const field = (this.host.nativeElement as HTMLElement).querySelector<HTMLElement>(
        '.knowledge-mode-note__topic input, .knowledge-mode-note__topic textarea'
      );
      field?.focus();
    });
  }

}
